import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { pct, ratioTone, fmtCount, RATIO_TONES, EMPTY } from './objectiveFormat';

export const BLOCKS = [
  { key: 'apd',  label: 'APD',  title: 'APD - A/B'          },
  { key: 'shop', label: 'SHOP', title: 'Shop drawings'      },
  { key: 'erd',  label: 'ERD',  title: 'Erection drawings'  },
];

// Two headline metrics, each followed by the two ways a job can fail to reach
// it. "Pending" is deliberately its own line: a job with no release date yet
// has not missed anything, and folding it into "delayed" is what makes a
// hand-made version of this report read worse than reality.
const METRICS = [
  { key: 'meet_schedule',     ratioKey: 'meet_schedule_ratio',  label: 'Eng Dept meet/earlier CSD schedule',           primary: true },
  { key: 'delayed_schedule',  label: 'Delayed vs CSD schedule' },
  { key: 'pending_schedule',  label: 'No schedule given yet' },
  { key: 'meet_committed',    ratioKey: 'meet_committed_ratio', label: 'Eng Dept meet/earlier CSD committed schedule', primary: true, graded: true },
  { key: 'delayed_committed', label: 'Delayed vs committed schedule' },
  { key: 'pending_committed', label: 'Not released yet' },
];

const CELL = 'tw-px-3 tw-py-2 tw-border-t tw-border-slate-100';

const MetricCell = ({ stats, metric }) => {
  const count = stats?.[metric.key];
  const ratio = metric.ratioKey ? stats?.[metric.ratioKey] : null;

  if (!stats || stats.total === 0) {
    return <td className={`${CELL} tw-text-right tw-text-slate-300`}>{EMPTY}</td>;
  }

  return (
    <td className={`${CELL} tw-text-right tw-tabular-nums`}>
      <span className={metric.primary ? 'tw-font-bold tw-text-slate-800' : 'tw-text-slate-500'}>
        {fmtCount(count)}
      </span>
      {metric.ratioKey && (
        <span className="tw-ml-2 tw-font-semibold" style={{ color: metric.graded ? ratioTone(ratio) : RATIO_TONES.neutral }}>
          {pct(ratio)}
        </span>
      )}
    </td>
  );
};
MetricCell.propTypes = { stats: PropTypes.object, metric: PropTypes.object.isRequired };

/**
 * The per-CSE panel of the year-end objective sheet: six metrics down, the
 * three drawing stages across.
 */
const ObjectiveMatrix = ({ group }) => (
  <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white">
    <table className="tw-w-full tw-text-xs">
      <thead>
        <tr className="tw-bg-slate-50">
          <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide">Metric</th>
          {BLOCKS.map((block) => (
            <th key={block.key} title={block.title} className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide">
              {block.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr className="tw-bg-emerald-50/60">
          <td className={`${CELL} tw-font-bold tw-text-slate-700`}>TOTAL (jobs in scope)</td>
          {BLOCKS.map((block) => (
            <td key={block.key} className={`${CELL} tw-text-right tw-font-bold tw-tabular-nums tw-text-slate-800`}>
              {fmtCount(group?.[block.key]?.total)}
            </td>
          ))}
        </tr>
        {METRICS.map((metric) => (
          <tr key={metric.key} className={metric.primary ? 'tw-bg-white' : 'tw-bg-slate-50/40'}>
            <td className={`${CELL} ${metric.primary ? 'tw-font-semibold tw-text-slate-700' : 'tw-text-slate-500 tw-pl-6'}`}>
              {metric.label}
            </td>
            {BLOCKS.map((block) => (
              <MetricCell key={block.key} stats={group?.[block.key]} metric={metric} />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

ObjectiveMatrix.propTypes = { group: PropTypes.object };

export default memo(ObjectiveMatrix);
